import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import cn from 'classnames';
import { Button, OTPInput } from 'components';
import { useAuth } from 'contexts/AuthContext';

interface EmailVerificationProps {
  email: string;
  message?: string;
  onVerificationComplete?: () => void;
}

export const EmailVerification: React.FC<EmailVerificationProps> = ({
  email,
  message,
  onVerificationComplete
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { verifyEmailCode, resendEmailCode, clearEmailVerification } = useAuth();

  // Estados del componente
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [attempts, setAttempts] = useState(0);
  const [fadeIn, setFadeIn] = useState(false);
  const [resetTrigger, setResetTrigger] = useState(0);
  const maxAttempts = 3;

  // Efecto para animación de entrada
  useEffect(() => {
    setTimeout(() => setFadeIn(true), 300);
  }, []);

  // Timer para countdown de reenvío
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  // Manejar completado del OTP
  const handleOTPComplete = async (code: string) => {
    try {
      setLoading(true);
      await verifyEmailCode(code, email);

      toast.success(t('emailVerificationSuccess'));
      onVerificationComplete?.();
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || error?.message || '';
      const errorData = error?.response?.data?.data || {};
      
      // Verificar si es un código vencido ANTES de incrementar intentos
      if (errorData.codeExpired && errorData.newCodeSent) {
        toast.success('Código vencido. Te hemos enviado un nuevo código al email.');
        setAttempts(0); // Resetear intentos ya que se envió un nuevo código
        setCountdown(60); // Activar countdown para reenvío
        setResetTrigger(prev => prev + 1); // Trigger reset del OTP input
        return;
      }

      // Manejo normal de errores (código incorrecto) - SOLO aquí incrementamos intentos
      const newAttempts = attempts + 1;
      setAttempts(newAttempts);

      if (newAttempts >= maxAttempts) {
        toast.error(t('tooManyAttempts'));
      } else {
        const remainingAttempts = maxAttempts - newAttempts;
        toast.error(t('invalidCodeWithAttempts', { count: remainingAttempts }));
      }

      setResetTrigger(prev => prev + 1); // Trigger reset del OTP input
    } finally {
      setLoading(false);
    }
  };

  // Manejar reenvío de código
  const handleResend = async () => {
    try {
      setResendLoading(true);
      await resendEmailCode(email);
      setCountdown(60); // 60 segundos para poder reenviar nuevamente
      setAttempts(0); // Resetear intentos
      toast.success(t('codeResent'));
    } catch (error: any) {
      toast.error(t('resendError'));
    } finally {
      setResendLoading(false);
    }
  };

  // Volver al login
  const handleBackToLogin = () => {
    clearEmailVerification();
    navigate('/auth/login');
  };

  return (
    <div>
      {/* Back to login button - positioned to keep title at same height as login */}
      <div className={cn("mb-[40px] enter-y delay-50", {fadeIn})}>
        <button 
          type="button"
          onClick={handleBackToLogin}
          className="text-sm text-gray-500 flex items-center transition-all"
        >
          <svg className="w-4 h-4 mr-1 c-orange" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Volver al login
        </button>
      </div>

      {/* Header - compensate for button height to match login title position */}
      <h2 className={cn("mb-[15px] text-3xl c-orange font-light enter-y delay-100", {fadeIn})}>
        Verificar Ingreso
      </h2>
      
      {/* Info text */}
      <div className={cn("mb-6 text-left enter-y delay-150", {fadeIn})}>
        <p className="text-gray-600 text-base mb-1">
          Código de verificación enviado a:
        </p>
        <p className="font-semibold text-gray-800 text-base">
          {email}
        </p>
      </div>

      {/* OTP Input */}
      <div className={cn("mb-6 enter-y delay-200", {fadeIn})}>
        <div className="flex justify-start">
          <OTPInput
            length={6}
            onComplete={handleOTPComplete}
            disabled={loading || attempts >= maxAttempts}
            autoFocus
            resetTrigger={resetTrigger}
            className="mb-4"
          />
        </div>
      </div>
      
      {/* Loading state */}
      {loading && (
        <div className={cn("flex justify-center mb-4 enter-y delay-300", {fadeIn})}>
          <div className="flex items-center text-sm text-gray-600">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-orange-500 mr-2"></div>
            {t('verifying')}...
          </div>
        </div>
      )}
      
      {/* Timer and Resend button on same line */}
      <div className={cn("flex justify-start items-center gap-8 mb-4 enter-y delay-400", {fadeIn})}>
        <span className="text-sm text-gray-500">
          Expira en 5 minutos
        </span>
        <button 
          type="button" 
          onClick={handleResend}
          disabled={countdown > 0 || resendLoading || attempts >= maxAttempts}
          className={cn(
            "text-sm underline hover:no-underline transition-all",
            {
              "c-orange cursor-pointer": !(countdown > 0 || resendLoading || attempts >= maxAttempts),
              "text-gray-400 cursor-not-allowed": countdown > 0 || resendLoading || attempts >= maxAttempts
            }
          )}
        >
          {countdown > 0 ? t('resendIn', { seconds: countdown }) : 'Reenviar código'}
        </button>
      </div>

      {/* Contador de intentos */}
      {attempts > 0 && attempts < maxAttempts && (
        <p className={cn("text-sm text-orange-600 mt-4 text-center enter-y delay-600", {fadeIn})}>
          {t('attemptsRemaining', { count: maxAttempts - attempts })}
        </p>
      )}

      {/* Demasiados intentos */}
      {attempts >= maxAttempts && (
        <p className={cn("text-sm text-red-600 mt-4 text-center enter-y delay-600", {fadeIn})}>
          {t('tooManyAttemptsMessage')}
        </p>
      )}

    </div>
  );
};